/**
 * Mass Messages → Create: tot twaalf Mass Messages tegelijk plannen (eerst drie).
 *
 * 03-10-2026 (Jordy). Drie slots naast elkaar (op een smal scherm onder
 * elkaar). Elk slot is precies één model en heeft hetzelfde formulier als het
 * Mass DM-venster in Chats (`MassDmFormulier.tsx`): methode, tekst met emoji,
 * content uit de bibliotheek (gratis of betaald, met minimumprijs), filters,
 * vertaling vooraf ("Fans receive (NL)"), plus datum en tijd in Amsterdamse
 * tijd.
 *
 * "Schedule all" plant elk ingevuld slot; lege slots tellen niet mee. Eerst
 * controleert het scherm zelf, dan de server (`schedule-batch` met dry_run:
 * rechten, prijs, bibliotheek, tijdstip), dan volgt één bevestiging met alles
 * wat er gepland wordt. De server plant alles-of-niets in één transactie: is
 * één slot fout, dan wordt er niets gepland en staat de fout bij dat slot.
 *
 * Er wordt hier niets verstuurd. "Preview count" is een dry run.
 *
 * 04-10-2026 (Jordy):
 *   - (vervallen, zie hieronder) bovenaan één modelkeuze voor alle slots.
 *   - datum en tijd staan nooit leeg: een nieuw, gewist of net gepland slot
 *     begint op nu, naar boven afgerond op 5 minuten (lib/tijdkeuze.ts). Met
 *     snelknoppen (Now, +15 min, +30 min, +1 hour) en een keuzelijst per
 *     kwartier (sinds 04-10 avond zonder typveld: components/TijdKiezer.tsx). Is de tijd intussen voorbij, dan
 *     staat bij de melding een knop die hem in één klik op nu zet.
 *   - de prijs is zichtbaar en aan te passen in het slot (MassDmVelden), in
 *     de preview en in de bevestiging: wat één fan betaalt, en bij meerdere
 *     items dat het één prijs voor de hele bundel is.
 *
 * 04-10-2026 (Jordy): "maak hier maar 12 stuks van, boven elke 3 een kiezer
 * met modellen zodat we per model er 3 kunnen aanmaken; de kiezer blijft ook
 * staan per venster". Dus:
 *   - twaalf slots in vier groepen van drie. Boven elke groep een modelkiezer
 *     die het model in de drie slots van die groep zet; elk slot houdt zijn
 *     eigen modelkiezer (per slot blijft wijzigen kunnen).
 *   - de oude kiezer "Model for all slots" is weg: met vier groepskiezers zette
 *     hij één model in alle twaalf slots (en wiste daarbij content), precies
 *     het tegenovergestelde van "per model drie".
 *   - een slot telt als ingevuld als er tekst of content in staat. Alleen een
 *     model (van de groepskiezer) is nog leeg en wordt overgeslagen; anders
 *     zou elke groep met een model meteen drie fouten geven.
 *   - "Copy from slot N" kopieert het eerste slot van de eigen groep; "Copy
 *     group 1" zet groep 1 in een andere groep, met het model van die groep
 *     (content alleen bij hetzelfde model: de bibliotheek is per model).
 *   - groep 1 staat open, de andere groepen tonen alleen hun kop tot je er een
 *     model kiest of ze openklapt. Een dichte groep tekent geen formulieren, dus
 *     ook geen vertaalverzoeken; een groep met een fout klapt vanzelf open.
 *   - "Schedule all" staat onderaan vast in beeld; de bevestiging groepeert
 *     per model.
 *
 * 04-10-2026 (Jordy): "Save as draft", per slot en voor alle gevulde slots.
 * Een concept wordt nooit verstuurd; het staat daarna op de tab Drafts, waar
 * een admin of owner het goedkeurt (MassaConcepten.tsx). Dezelfde controles
 * als plannen, behalve de tijd: die mag voor een concept leeg of voorbij zijn.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Field, Modal } from '../components/Modal'
import { Avatar, Plat, Seg, Table, Tag } from '../components/ui'
import {
  MassDmBerichtVoorbeeld,
  MassDmTelling,
  MassDmVelden,
  bereikTekst,
  leegMassDm,
  massDmAfgeleid,
  massDmFouten,
  massVerzoek,
  methodeNaam,
  modelKeuzes,
  type MassDmAfgeleid,
  type MassDmWaarden,
  type MassPreview,
  type Methode,
  type ModelKeuze,
} from '../components/MassDmFormulier'
import { useApi } from '../lib/useApi'
import { useActions } from '../lib/actions'
import { api, ApiError } from '../lib/api'
import { AMSTERDAM, alsZone, zetOm, zoneLabel, zoneNaarUtc } from '../lib/amsterdam'
import { MIN_VOORUIT_MS, standaardMoment, standaardNieuwMoment, tijdFout } from '../lib/tijdkeuze'
import { useBijZoneWissel, useMijnZone } from '../lib/tijdzone'
import { TijdKiezer } from '../components/TijdKiezer'
import { ZoneKeuze } from '../components/ZoneKeuze'
import type { Persona } from './Models'

// 04-10-2026 (Jordy): vier groepen van drie slots, één model per groep. De
// server neemt er ook hooguit twaalf in één keer (MASS_SCHEDULE_BATCH_MAX).
const GROEPEN = 4
const PER_GROEP = 3
const AANTAL_SLOTS = GROEPEN * PER_GROEP
const BATCH = '/api/admin/conversations/mass-message/schedule-batch'
const CONCEPTEN = '/api/admin/conversations/mass-message/drafts'

/** In welke groep (0–3) slot `i` (0–11) staat. */
const groepVan = (i: number) => Math.floor(i / PER_GROEP)
/** De slotindexen (0–11) van groep `g`. */
const slotsVan = (g: number) => Array.from({ length: PER_GROEP }, (_, k) => g * PER_GROEP + k)

interface Slot {
  w: MassDmWaarden
  datum: string
  tijd: string
  preview: MassPreview | null
  voorbeeldOpen: boolean
  /** Wat de server bij de laatste poging over dit slot zei. */
  serverFout: string
  /** Telt op bij wissen en kopiëren: het formulier begint dan opnieuw. */
  versie: number
}

function leegSlot(methode: Methode, versie = 0, modelSleutel = '', zone: string = AMSTERDAM): Slot {
  // 04-10-2026 (Jordy): nooit leeg en nooit een oude datum, maar nu (naar boven
  // afgerond op 5 minuten, in Amsterdamse tijd).
  // 06-10-2026 (Jordy): nu + 15 minuten (STANDAARD_VOORUIT_MINUTEN).
  // 07-10-2026 (Jordy): in de tijdzone van de pagina (standaard die van de gebruiker).
  const { datum, tijd } = standaardNieuwMoment(undefined, zone)
  return {
    // 04-10-2026: een gewist of net gepland slot houdt het model van zijn
    // groep; de groepskiezer blijft staan.
    w: { ...leegMassDm(methode), modelSleutel }, datum, tijd, preview: null,
    voorbeeldOpen: false, serverFout: '', versie,
  }
}

/** Staat er iets in dit slot dat gepland kan worden (tekst of content)? */
function heeftInhoud(w: MassDmWaarden): boolean {
  // 04-10-2026 (Jordy): alleen een model (van de groepskiezer) telt niet; zo'n
  // slot is leeg en wordt overgeslagen, net als een slot zonder iets.
  return Boolean(w.tekst.trim() || w.media?.items.length)
}

/** Wat het scherm van één slot vindt: ingevuld, fouten, moment in UTC. */
function beoordeel(slot: Slot, keuzes: ModelKeuze[], magBetaald: boolean, zone: string) {
  const a = massDmAfgeleid(slot.w, keuzes, magBetaald)
  const gevuld = heeftInhoud(slot.w)
  const meldingen = massDmFouten(slot.w, a)
  // 07-10-2026 (Jordy): datum en tijd gelden in de gekozen zone; `moment` is
  // het absolute verzendmoment in UTC (dat gaat naar de server).
  const moment = zoneNaarUtc(slot.datum, slot.tijd, zone)
  // Dezelfde grenzen als de server: niet in het verleden, minstens 2 minuten
  // vooruit, hoogstens 90 dagen (lib/tijdkeuze.ts).
  const tijdMelding = tijdFout(slot.datum, slot.tijd, undefined, zone)
  if (tijdMelding) meldingen.push(tijdMelding)
  // Voorbij (of te dichtbij): bij de melding staat dan een knop "Set to now".
  const teVroeg = Boolean(moment) && Date.parse(moment as string) < Date.now() + MIN_VOORUIT_MS
  return { a, gevuld, meldingen, moment, teVroeg }
}

/** Het verzoek voor de server: de velden van het formulier, plus content en tijd. */
function verzoekVoor(slot: Slot, a: MassDmAfgeleid, moment: string) {
  return {
    ...a.basis,
    content_ids: a.metMedia ? (slot.w.media?.items ?? []).map((it) => it.id) : [],
    // 05-10-2026: de keuze uit het formulier; zonder content weigert de server
    // inplannen (400), een concept bewaart hem.
    is_paid: a.gekozenBetaald,
    price: a.gekozenBetaald ? a.prijsGetal : 0,
    scheduled_at: moment,
  }
}

/** Eén regel in de bevestiging en in de samenvatting. */
interface Regel {
  nr: number
  model: ModelKeuze | null
  methode: Methode
  moment: string
  betaald: boolean
  prijs: number
  /** 04-10-2026: "€15,00 per fan · 3 items, one price" (lib/massPrijs.ts). */
  prijsRegel: string
  metMedia: boolean
  aantalMedia: number
  echteFans: boolean
  bereik: string | null
  verzoek: ReturnType<typeof verzoekVoor>
}

export function MassaCreate({
  magDirect,
  magNative,
  magBetaald,
  magOverzicht,
}: {
  magDirect: boolean
  magNative: boolean
  magBetaald: boolean
  /** Mag dit account de tab Overview zien (voor de link na het plannen)? */
  magOverzicht: boolean
}) {
  const personas = useApi<Persona[]>('/api/admin/models/personas')
  const keuzes = useMemo(() => modelKeuzes(personas.data ?? []), [personas.data])
  // 05-10-2026 (Jordy): Platform Mass DM is voor iedereen de standaard
  // (nieuw slot, Clear, kopiëren, nieuw concept).
  const standaard: Methode = magNative ? 'native' : 'direct'
  // 07-10-2026 (Jordy): één tijdzone voor de hele pagina, standaard die van de
  // gebruiker (Asia/Manila voor Max en Rosie). Wisselt de zone (keuze, of /me
  // die binnenkomt), dan houden de slots hetzelfde moment: 16:50 Amsterdam
  // wordt 22:50 Manila, er verschuift niets.
  const mijnZone = useMijnZone()
  const [gekozenZone, setGekozenZone] = useState<string | null>(null)
  const zone = gekozenZone ?? mijnZone
  const [slots, setSlots] = useState<Slot[]>(
    () => Array.from({ length: AANTAL_SLOTS }, () => leegSlot(standaard, 0, '', zone)))
  useBijZoneWissel(zone, (van, naar) => setSlots((oud) => oud.map((s) => (
    { ...s, ...zetOm({ datum: s.datum, tijd: s.tijd }, van, naar) }))))
  // 04-10-2026 (Jordy): het model per groep, en welke groepen open staan. Groep
  // 1 staat open; de rest toont alleen de kop tot er een model gekozen wordt.
  const [groepModel, setGroepModel] = useState<string[]>(() => Array(GROEPEN).fill(''))
  const [open, setOpen] = useState<boolean[]>(() => Array.from({ length: GROEPEN }, (_, g) => g === 0))
  // Pas na de eerste poging staan de meldingen er; een leeg formulier dat al
  // rood staat helpt niemand.
  const [geprobeerd, setGeprobeerd] = useState(false)
  const [bevestigen, setBevestigen] = useState<Regel[] | null>(null)
  const [getypt, setGetypt] = useState('')
  const [bezig, setBezig] = useState(false)
  const [gepland, setGepland] = useState<Regel[] | null>(null)
  /** 04-10-2026: hoeveel concepten er net zijn opgeslagen (voor de melding bovenaan). */
  const [bewaard, setBewaard] = useState<number | null>(null)
  const { run, busy, notify } = useActions()

  const zetSlot = useCallback((i: number, patch: Partial<Slot>) => {
    setSlots((oud) => oud.map((s, j) => (j === i ? { ...s, ...patch } : s)))
  }, [])
  const zetters = useMemo(() => Array.from({ length: AANTAL_SLOTS }, (_, i) =>
    (patch: Partial<MassDmWaarden>) => setSlots((oud) => oud.map((s, j) => (
      j === i ? { ...s, w: { ...s.w, ...patch } } : s)))), [])
  const wijzigers = useMemo(() => Array.from({ length: AANTAL_SLOTS }, (_, i) =>
    (soort: 'alles' | 'bevestiging') => setSlots((oud) => oud.map((s, j) => (
      j === i ? { ...s, serverFout: '', ...(soort === 'alles' ? { preview: null } : {}) } : s)))), [])

  const beoordeeld = slots.map((s) => beoordeel(s, keuzes, magBetaald, zone))
  const keuzeVan = (sleutel: string) => keuzes.find((k) => k.sleutel === sleutel) ?? null

  // 04-10-2026 (Jordy): met twaalf slots staat een fout vaak buiten beeld. Na
  // een mislukte poging scrolt het scherm naar de melding van het eerste foute
  // slot (pas na het tekenen: de groep klapt misschien net open).
  const [scrollNaar, setScrollNaar] = useState<{ nr: number } | null>(null)
  useEffect(() => {
    if (!scrollNaar) return
    const t = window.setTimeout(() => {
      const doel = document.querySelector(`.massa-slot[data-slot="${scrollNaar.nr}"] .massa-slot-errors`)
        ?? document.querySelector(`.massa-slot[data-slot="${scrollNaar.nr}"]`)
      doel?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }, 50)
    return () => window.clearTimeout(t)
  }, [scrollNaar])

  /** Klap de groepen open waarin deze slots (1–12) staan, en ga naar de eerste. */
  function openGroepenVan(nrs: number[]) {
    if (!nrs.length) return
    setOpen((oud) => oud.map((o, g) => o || nrs.some((nr) => groepVan(nr - 1) === g)))
    setScrollNaar({ nr: Math.min(...nrs) })
  }

  /** Kopieer een slot naar slot `i` (alles: model, tekst, content, tijd). */
  function kopieerNaar(i: number, bron: number) {
    setSlots((oud) => oud.map((s, j) => (j === i ? {
      ...oud[bron],
      w: { ...oud[bron].w, vertaalBezig: false, vertaalFout: '' },
      preview: null, voorbeeldOpen: false, serverFout: '', versie: s.versie + 1,
    } : s)))
  }
  function wis(i: number) {
    setSlots((oud) => oud.map((s, j) => (
      j === i ? leegSlot(standaard, s.versie + 1, groepModel[groepVan(i)], zone) : s)))
  }

  // 04-10-2026 (Jordy): één model per groep van drie. Kiezen (of opnieuw
  // toepassen) zet het in de drie slots van die groep, zonder te vragen; een
  // slot met een ander model verliest zijn content (die hoort bij de
  // bibliotheek van dat model), net als bij het wisselen in het slot zelf. De
  // andere groepen blijven zoals ze zijn.
  function modelVoorGroep(g: number, sleutel: string) {
    setGroepModel((oud) => oud.map((m, h) => (h === g ? sleutel : m)))
    if (!sleutel) return
    setOpen((oud) => oud.map((o, h) => o || h === g))
    setSlots((oud) => oud.map((s, j) => (groepVan(j) !== g || s.w.modelSleutel === sleutel ? s : {
      ...s, w: { ...s.w, modelSleutel: sleutel, media: null }, preview: null, serverFout: '',
    })))
  }

  // 04-10-2026 (Jordy): groep 1 naar een andere groep kopiëren (dezelfde
  // berichten voor een ander model). Alleen de ingevulde slots van groep 1; het
  // model blijft dat van de doelgroep. Content gaat alleen mee bij hetzelfde
  // model, want de bibliotheek is per model.
  function kopieerGroep1(g: number) {
    const doel = slotsVan(g)
    const bronnen = slotsVan(0).filter((i) => beoordeeld[i].gevuld)
    if (!bronnen.length) return
    const overschrijft = bronnen.some((i) => beoordeeld[doel[i]].gevuld)
    if (overschrijft && !window.confirm(`Overwrite the filled slots in group ${g + 1} with group 1?`)) return
    let zonderContent = 0
    const nieuw = slots.map((s, j) => {
      if (groepVan(j) !== g) return s
      const bron = slots[j - g * PER_GROEP]
      if (!heeftInhoud(bron.w)) return s
      const model = groepModel[g] || s.w.modelSleutel || bron.w.modelSleutel
      const zelfde = model === bron.w.modelSleutel
      if (!zelfde && bron.w.media?.items.length) zonderContent += 1
      return {
        ...bron,
        w: { ...bron.w, modelSleutel: model, media: zelfde ? bron.w.media : null, vertaalBezig: false, vertaalFout: '' },
        preview: null, voorbeeldOpen: false, serverFout: '', versie: s.versie + 1,
      }
    })
    setSlots(nieuw)
    setOpen((oud) => oud.map((o, h) => o || h === g))
    notify(zonderContent
      ? `Copied group 1 into group ${g + 1}. Photos and videos were left out: the library is per model.`
      : `Copied group 1 into group ${g + 1}.`)
  }

  /** Datum en tijd van een slot zetten (snelknop, keuzelijst of "Set to now"). */
  function zetMoment(i: number, m: { datum: string; tijd: string }) {
    zetSlot(i, { datum: m.datum, tijd: m.tijd, serverFout: '' })
  }

  /** Zet de fouten van de server bij de slots waar ze horen. */
  function toonServerFouten(e: unknown, regels: Regel[], wat = 'scheduled') {
    const data = e instanceof ApiError ? (e.data as { errors?: { index: number; detail: string }[] } | null) : null
    const lijst = data?.errors ?? []
    if (lijst.length) {
      // `index` telt de items in het verzoek (lege slots gaan niet mee); de
      // regel weet bij welk slot (1–12) dat item hoort.
      setSlots((oud) => oud.map((s, j) => {
        const f = lijst.find((x) => regels[x.index]?.nr === j + 1)
        return { ...s, serverFout: f ? f.detail : '' }
      }))
      openGroepenVan(lijst.map((x) => regels[x.index]?.nr).filter((nr): nr is number => Boolean(nr)))
      notify(`Nothing was ${wat}. See the slots marked in red.`, false)
    } else {
      notify(`Nothing was ${wat}: ${e instanceof Error ? e.message : String(e)}`, false)
    }
  }

  /**
   * 04-10-2026 (Jordy): ingevulde slots als concept opslaan (`alleen` = één
   * slot, 0–11). Dezelfde controles als plannen, behalve de tijd: een
   * concept mag een lege of voorbije tijd hebben (de server zet leeg op nu).
   */
  async function bewaarConcepten(alleen?: number) {
    setGeprobeerd(true)
    setGepland(null)
    setBewaard(null)
    const regels: Regel[] = []
    const fout: number[] = []
    slots.forEach((s, i) => {
      if (alleen != null && i !== alleen) return
      const b = beoordeeld[i]
      if (!b.gevuld) return
      if (massDmFouten(s.w, b.a, { concept: true }).length) { fout.push(i + 1); return }
      const moment = b.moment ?? ''
      regels.push({
        nr: i + 1, model: b.a.gekozen, methode: s.w.methode ?? standaard, moment,
        betaald: b.a.betaaldMedia, prijs: b.a.prijsGetal, prijsRegel: b.a.prijsRegelTekst,
        metMedia: b.a.metMedia, aantalMedia: s.w.media?.items.length ?? 0,
        echteFans: b.a.naarEchteFans, bereik: null, verzoek: verzoekVoor(s, b.a, moment),
      })
    })
    if (fout.length) {
      openGroepenVan(fout)
      notify(`Nothing was saved. Fix slot ${fout.join(', ')} (marked in red).`, false)
      return
    }
    if (!regels.length) {
      notify(alleen != null ? 'This slot is empty.' : 'Fill in at least one slot.', false)
      return
    }
    setBezig(true)
    try {
      await api.post(CONCEPTEN, { items: regels.map((r) => r.verzoek) })
      setBewaard(regels.length)
      setGeprobeerd(false)
      const nrs = new Set(regels.map((r) => r.nr - 1))
      setSlots((oud) => oud.map((s, j) => (
        nrs.has(j) ? leegSlot(standaard, s.versie + 1, groepModel[groepVan(j)], zone) : { ...s, serverFout: '' })))
      notify(regels.length === 1 ? 'Saved 1 draft. Nothing was sent.' : `Saved ${regels.length} drafts. Nothing was sent.`)
    } catch (e) {
      toonServerFouten(e, regels, 'saved')
    } finally {
      setBezig(false)
    }
  }

  async function planAlles() {
    setGeprobeerd(true)
    setGepland(null)
    const regels: Regel[] = []
    const fout: number[] = []
    slots.forEach((s, i) => {
      const b = beoordeeld[i]
      if (!b.gevuld) return
      if (b.meldingen.length || !b.moment) { fout.push(i + 1); return }
      regels.push({
        nr: i + 1,
        model: b.a.gekozen,
        methode: s.w.methode ?? standaard,
        moment: b.moment,
        betaald: b.a.betaaldMedia,
        prijs: b.a.prijsGetal,
        prijsRegel: b.a.prijsRegelTekst,
        metMedia: b.a.metMedia,
        aantalMedia: s.w.media?.items.length ?? 0,
        echteFans: b.a.naarEchteFans,
        bereik: s.preview ? bereikTekst(s.preview) : null,
        verzoek: verzoekVoor(s, b.a, b.moment),
      })
    })
    if (fout.length) {
      openGroepenVan(fout)
      notify(`Nothing was scheduled. Fix slot ${fout.join(', ')} (marked in red).`, false)
      return
    }
    if (!regels.length) {
      notify('Fill in at least one slot.', false)
      return
    }
    // Eerst de server laten controleren (rechten, prijs, bibliotheek, tijd),
    // zonder iets op te slaan. Pas daarna de bevestiging.
    setBezig(true)
    try {
      await api.post(BATCH, { items: regels.map((r) => r.verzoek), dry_run: true })
      setSlots((oud) => oud.map((s) => ({ ...s, serverFout: '' })))
      setGetypt('')
      setBevestigen(regels)
    } catch (e) {
      toonServerFouten(e, regels)
    } finally {
      setBezig(false)
    }
  }

  async function bevestig() {
    const regels = bevestigen
    if (!regels) return
    setBezig(true)
    try {
      await api.post(BATCH, { items: regels.map((r) => r.verzoek), dry_run: false })
      setBevestigen(null)
      setGepland(regels)
      setGeprobeerd(false)
      setSlots((oud) => oud.map((s, j) => leegSlot(standaard, s.versie + 1, groepModel[groepVan(j)], zone)))
      notify(regels.length === 1 ? '1 Mass Message scheduled.' : `${regels.length} Mass Messages scheduled.`)
    } catch (e) {
      setBevestigen(null)
      toonServerFouten(e, regels)
    } finally {
      setBezig(false)
    }
  }

  const echteFans = (bevestigen ?? []).some((r) => r.echteFans)
  const magBevestigen = !bezig && (!echteFans || getypt.trim().toUpperCase() === 'SEND')
  const aantalGevuld = beoordeeld.filter((b) => b.gevuld).length
  const groep1Gevuld = slotsVan(0).some((i) => beoordeeld[i].gevuld)

  /** Eén slot: kop, methode, het gedeelde formulier, tijd, preview, meldingen. */
  function slotKaart(i: number) {
    const slot = slots[i]
    const b = beoordeeld[i]
    const meldingen = [
      ...(geprobeerd && b.gevuld ? b.meldingen : []),
      ...(slot.serverFout ? [slot.serverFout] : []),
    ]
    const nr = i + 1
    const eerste = groepVan(i) * PER_GROEP
    return (
      <div key={i} className={meldingen.length ? 'card massa-slot invalid' : 'card massa-slot'}
           data-slot={nr}>
        <div className="massa-slot-kop">
          {b.a.gekozen ? <Avatar name={b.a.gekozen.label} src={b.a.gekozen.avatar_url} size={26} /> : null}
          <b style={{ flex: 1 }}>
            Slot {nr}
            {b.a.gekozen ? <span className="mut2" style={{ fontWeight: 500 }}> · {b.a.gekozen.label}</span> : null}
          </b>
          {/* 04-10-2026 (Jordy): de prijs per fan meteen zichtbaar. */}
          {b.a.metMedia ? (
            <span className={b.a.betaaldMedia ? 'tag acc' : 'tag mut'} data-testid="slot-prijs">
              {b.a.prijsRegelTekst}
            </span>
          ) : null}
          {/* 04-10-2026: een slot met alleen een model is leeg en gaat niet mee. */}
          {!b.gevuld && slot.w.modelSleutel ? <span className="tag mut" data-testid="slot-leeg">Empty · skipped</span> : null}
          {i !== eerste ? (
            <button className="btn ghost sm" type="button" title="Copy everything from the first slot of this group"
                    onClick={() => kopieerNaar(i, eerste)}>
              Copy from slot {eerste + 1}
            </button>
          ) : null}
          <button className="btn ghost sm" type="button" onClick={() => wis(i)}>Clear</button>
        </div>

        <Field label="Method">
          {magDirect && magNative ? (
            <Seg
              options={[
                { value: 'direct', label: 'Dashboard Mass Message' },
                { value: 'native', label: 'Platform Mass DM' },
              ]}
              value={slot.w.methode ?? standaard}
              onChange={(v) => {
                zetSlot(i, { preview: null, serverFout: '' })
                zetters[i]({ methode: v as Methode })
              }}
            />
          ) : (
            <span>{methodeNaam(standaard)}</span>
          )}
        </Field>

        <MassDmVelden aiSuggesties
          key={`${i}-${slot.versie}`}
          w={slot.w}
          zet={zetters[i]}
          keuzes={keuzes}
          magBetaald={magBetaald}
          onWijzig={wijzigers[i]}
          echteFansHint="Real fans. You type SEND once, when you confirm “Schedule all”."
        />

        {/* 04-10-2026 (Jordy): alleen de keuzelijst, geen typveld (TijdKiezer). */}
        <TijdKiezer datum={slot.datum} tijd={slot.tijd} naam={`Slot ${nr}`} zone={zone}
                    onChange={(m) => zetMoment(i, m)} />

        <MassDmTelling preview={slot.preview} />

        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button className="btn sm" type="button"
                  disabled={!b.a.gekozen || busy !== null}
                  onClick={() => run({
                    label: `Preview count slot ${nr}`,
                    // Een dry run via de worker: er gaat niets uit.
                    request: () => massVerzoek<MassPreview>(slot.w, b.a, true)
                      .then((p) => { zetSlot(i, { preview: p }); return p }),
                    successText: 'Preview only — nothing was sent.',
                  })}>
            Preview count
          </button>
          <button className="btn sm" type="button"
                  disabled={!slot.w.tekst.trim() && !b.a.metMedia}
                  onClick={() => zetSlot(i, { voorbeeldOpen: !slot.voorbeeldOpen })}>
            {slot.voorbeeldOpen ? 'Hide preview' : 'Preview message'}
          </button>
          {/* 04-10-2026 (Jordy): alleen dit slot als concept bewaren. */}
          <button className="btn ghost sm" type="button" disabled={!b.gevuld || bezig || busy !== null}
                  data-testid="slot-concept" onClick={() => { void bewaarConcepten(i) }}>
            Save as draft
          </button>
        </div>

        {slot.voorbeeldOpen ? (
          <MassDmBerichtVoorbeeld w={slot.w} a={b.a} preview={slot.preview}
                                  onSluit={() => zetSlot(i, { voorbeeldOpen: false })} />
        ) : null}

        {meldingen.length ? (
          <ul className="massa-slot-errors" role="alert">
            {meldingen.map((m) => <li key={m}>{m}</li>)}
            {/* 04-10-2026 (Jordy): de tijd is intussen voorbij — in één
                klik op nu (de volgende 5 minuten) zetten. */}
            {geprobeerd && b.gevuld && b.teVroeg ? (
              <li className="massa-fix">
                <button className="btn sm" type="button" onClick={() => zetMoment(i, standaardMoment(undefined, zone))}>
                  Set to now ({standaardMoment(undefined, zone).tijd})
                </button>
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>
    )
  }

  /** Eén groep van drie: de kop met de groepskiezer, daaronder de drie slots. */
  function groepBlok(g: number) {
    const idx = slotsVan(g)
    const sleutel = groepModel[g]
    const model = keuzeVan(sleutel)
    const gevuld = idx.filter((i) => beoordeeld[i].gevuld).length
    const anders = sleutel ? idx.filter((i) => slots[i].w.modelSleutel !== sleutel).length : 0
    const metFout = idx.some((i) => slots[i].serverFout || (geprobeerd && beoordeeld[i].gevuld && beoordeeld[i].meldingen.length))
    const isOpen = open[g]
    return (
      <section key={g} className="massa-groep" data-groep={g + 1}>
        <div className={metFout ? 'card massa-groep-kop invalid' : 'card massa-groep-kop'}>
          {model ? <Avatar name={model.label} src={model.avatar_url} size={30} /> : null}
          <div className="massa-groep-naam">
            <b>Group {g + 1}</b>
            <span className="mut2"> · slots {idx[0] + 1}–{idx[idx.length - 1] + 1}</span>
          </div>
          <select className="inp massa-groep-model" aria-label={`Group ${g + 1} model`}
                  value={sleutel} onChange={(e) => modelVoorGroep(g, e.target.value)}>
            <option value="">Choose a model for these 3 slots…</option>
            {keuzes.map((k) => <option key={k.sleutel} value={k.sleutel}>{k.label}</option>)}
          </select>
          {anders ? (
            <button className="btn sm" type="button" onClick={() => modelVoorGroep(g, sleutel)}
                    title="Set this model in all 3 slots of the group again">
              Apply to all 3
            </button>
          ) : null}
          <span className={gevuld ? 'tag acc' : 'tag mut'} data-testid="groep-stand">
            {gevuld} of {PER_GROEP} filled
          </span>
          {anders ? (
            <span className="tag warn" data-testid="groep-anders">
              {anders === 1 ? '1 slot uses' : `${anders} slots use`} another model
            </span>
          ) : null}
          {g > 0 ? (
            <button className="btn ghost sm" type="button" disabled={!groep1Gevuld}
                    title="Copy the filled slots of group 1 here, with this group’s model"
                    onClick={() => kopieerGroep1(g)}>
              Copy group 1
            </button>
          ) : null}
          <button className="btn ghost sm" type="button" aria-expanded={isOpen}
                  onClick={() => setOpen((oud) => oud.map((o, h) => (h === g ? !o : o)))}>
            {isOpen ? 'Hide slots' : 'Show slots'}
          </button>
        </div>
        {isOpen ? <div className="massa-slots">{idx.map((i) => slotKaart(i))}</div> : null}
      </section>
    )
  }

  return (
    <div className="massa-create">
      {bewaard ? (
        <div className="card row" style={{ marginBottom: 14, gap: 8 }} data-testid="massa-bewaard">
          <b style={{ flex: 1 }}>
            Saved {bewaard} draft{bewaard === 1 ? '' : 's'}. Drafts are never sent until an admin approves them.
          </b>
          <Link className="btn sm" to="/mass-messages?tab=drafts">View drafts</Link>
          <button className="btn ghost sm" type="button" onClick={() => setBewaard(null)}>Dismiss</button>
        </div>
      ) : null}

      {gepland ? (
        <div className="card" style={{ marginBottom: 14 }} data-testid="massa-gepland">
          <div className="row" style={{ marginBottom: 8, gap: 8 }}>
            <b style={{ flex: 1 }}>
              Scheduled {gepland.length} Mass Message{gepland.length === 1 ? '' : 's'}
            </b>
            {magOverzicht ? (
              <Link className="btn sm" to="/mass-messages?tab=overview">View in Overview</Link>
            ) : null}
            <button className="btn ghost sm" type="button" onClick={() => setGepland(null)}>Dismiss</button>
          </div>
          <RegelTabel regels={gepland} zone={zone} />
          <div className="mut2" style={{ fontSize: 12, marginTop: 8 }}>
            Each one runs on its own at its time and shows up under “Planned” in the Overview,
            where you can still cancel it.
          </div>
        </div>
      ) : null}

      <div className="mut2" style={{ fontSize: 12.5, marginBottom: 12 }}>
        Up to {AANTAL_SLOTS} Mass Messages in {GROEPEN} groups of {PER_GROEP}: pick a model per group,
        and change it per slot if you need to. Times are {zoneLabel(zone)} time. Empty slots are skipped;
        nothing is scheduled if a filled slot has a problem.
      </div>

      {/* 07-10-2026 (Jordy): de tijdzone van alle slots op deze pagina. */}
      <div className="row" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}
           data-testid="create-time-zone">
        <b style={{ fontSize: 12.5 }}>Time zone</b>
        <div style={{ width: 'auto', maxWidth: 260 }}>
          <ZoneKeuze value={zone} onChange={setGekozenZone} />
        </div>
        <span className="mut2" style={{ fontSize: 12 }}>
          Dates and times below are read in this zone. The send moment is exact; changing the zone keeps it.
        </span>
      </div>

      {Array.from({ length: GROEPEN }, (_, g) => groepBlok(g))}

      {/* 04-10-2026 (Jordy): met twaalf slots wordt de pagina lang; de knop
          blijft onderaan in beeld. */}
      <div className="card massa-actie">
        {/* De knop links: meldingen verschijnen rechtsonder en mogen hem niet bedekken. */}
        <button className="btn primary" type="button" disabled={bezig || busy !== null}
                onClick={() => { void planAlles() }}>
          {bezig && !bevestigen ? 'Checking…' : `Schedule all${aantalGevuld ? ` (${aantalGevuld})` : ''}`}
        </button>
        {/* 04-10-2026 (Jordy): alle gevulde slots als concept bewaren. */}
        <button className="btn" type="button" disabled={bezig || busy !== null || !aantalGevuld}
                data-testid="alles-concept" onClick={() => { void bewaarConcepten() }}>
          {`Save as draft${aantalGevuld ? ` (${aantalGevuld})` : ''}`}
        </button>
        <span className="mut2" style={{ fontSize: 12.5, flex: 1, minWidth: 160 }} data-testid="massa-telling">
          {aantalGevuld
            ? `${aantalGevuld} of ${AANTAL_SLOTS} slots filled.`
            : `All ${AANTAL_SLOTS} slots are empty.`}
        </span>
      </div>

      {bevestigen ? (
        <Modal
          title={`Schedule ${bevestigen.length} Mass Message${bevestigen.length === 1 ? '' : 's'}?`}
          desc={`Each one goes out on its own at the time shown (${zoneLabel(zone)} time). You can cancel them in the Overview until then.`}
          width={880}
          onClose={() => setBevestigen(null)}
          footer={
            <>
              <button className="btn ghost sm" type="button" onClick={() => setBevestigen(null)}>Cancel</button>
              <button className="btn primary sm" type="button" disabled={!magBevestigen}
                      onClick={() => { void bevestig() }}>
                {bezig ? 'Scheduling…' : `Schedule ${bevestigen.length}`}
              </button>
            </>
          }
        >
          <RegelTabel regels={bevestigen} metBereik zone={zone} />
          {bevestigen.some((r) => r.betaald) ? (
            <div className="mut2" style={{ fontSize: 12, marginBottom: 12 }} data-testid="prijs-uitleg">
              Prices are per fan. A Mass Message with several items has one price for the whole
              bundle: the fan pays it once and unlocks all items.
            </div>
          ) : null}
          {echteFans ? (
            <Field label="Type SEND to confirm" hint="At least one slot goes to real fans.">
              <input className="inp" value={getypt} placeholder="SEND"
                     onChange={(e) => setGetypt(e.target.value)} />
            </Field>
          ) : null}
        </Modal>
      ) : null}
    </div>
  )
}

/**
 * De slots als tabel, per model gegroepeerd: een kopregel per model (foto,
 * platform, naam, aantal), daaronder slot, methode, tijd, prijs, publiek (en
 * het bereik). 04-10-2026 (Jordy): met twaalf regels bleef een platte lijst
 * niet leesbaar. De volgorde naar de server blijft die van de slots.
 */
function RegelTabel({ regels, metBereik = false, zone = AMSTERDAM }: { regels: Regel[]; metBereik?: boolean; zone?: string }) {
  const groepen: { model: ModelKeuze | null; regels: Regel[] }[] = []
  for (const r of regels) {
    const sleutel = r.model?.sleutel ?? ''
    const bestaand = groepen.find((x) => (x.model?.sleutel ?? '') === sleutel)
    if (bestaand) bestaand.regels.push(r)
    else groepen.push({ model: r.model, regels: [r] })
  }
  const kolommen = metBereik ? 6 : 5
  return (
    <div style={{ marginBottom: 12 }}>
      <Table cols={[
        { key: 'nr', label: 'Slot' }, { key: 'me', label: 'Method' },
        { key: 'w', label: `When (${zoneLabel(zone)})` }, { key: 'p', label: 'Price' },
        { key: 'a', label: 'Audience' },
        ...(metBereik ? [{ key: 'r', label: 'Recipients' }] : []),
      ]}>
        {groepen.map((gr) => [
          <tr key={`m-${gr.model?.sleutel ?? ''}`} className="massa-regelgroep" data-testid="regel-groep">
            <td colSpan={kolommen}>
              <span className="row" style={{ gap: 8 }}>
                <Avatar name={gr.model?.label} src={gr.model?.avatar_url} size={22} />
                <Plat value={gr.model?.platform} />
                <b>{gr.model?.label.split(' — ')[0] ?? '—'}</b>
                <span className="mut2">
                  · {gr.regels.length} Mass Message{gr.regels.length === 1 ? '' : 's'}
                </span>
              </span>
            </td>
          </tr>,
          ...gr.regels.map((r) => (
            <tr key={r.nr} data-testid="regel">
              <td className="mono">{r.nr}</td>
              <td>{methodeNaam(r.methode)}</td>
              <td className="mono">
                {alsZone(r.moment, zone)}
                {/* 07-10-2026: bij een andere zone ook de Amsterdamse tijd. */}
                {zone !== AMSTERDAM ? <span className="mut2"> = {alsZone(r.moment, AMSTERDAM)} Amsterdam</span> : null}
              </td>
              <td data-testid="regel-prijs">
                {r.betaald ? <Tag kind="acc">{r.prijsRegel}</Tag> : <span className="mut">{r.prijsRegel}</span>}
              </td>
              <td>{r.echteFans ? <Tag kind="warn">Real fans</Tag> : <Tag kind="mut">Test accounts</Tag>}</td>
              {metBereik ? (
                <td className="mut2" style={{ fontSize: 12 }}>
                  {r.bereik ?? 'Not counted (use Preview count)'}
                </td>
              ) : null}
            </tr>
          )),
        ])}
      </Table>
    </div>
  )
}
